import React from "react";
import MDialog from "@/components/ui/atoms/MDialog";
import MAvatar from "@/components/ui/atoms/MAvatar";
import MInputField from "@/components/ui/atoms/MInputField";
import styles from "./MemberProfileDialog.module.css";
import { IconX, IconDog, IconCat } from "@tabler/icons-react";

import MBadge from "@/components/ui/atoms/MBadge";

import { formatPrettyDate } from "@/utils/date-utils";

export interface MemberProfileDialogProps {
  open: boolean;
  onClose: () => void;
  member: {
    id: string;
    avatar?: string;
    name: string;
    birthDetails?: string;
    mobile?: string;
    email?: string;
    totalDocuments?: number;
    lastDocumentDate?: string;
    emergencyCardReady?: boolean;
    isPet?: boolean;
    petType?: "cat" | "dog";
  };
  isOwner?: boolean;
  registrationLabel?: string;
}

import { useEffect, useState } from "react";
import { useDocument } from "@/hooks/useDocument";

const MemberProfileDialog: React.FC<MemberProfileDialogProps> = ({
  open,
  onClose,
  member,
  isOwner,
  registrationLabel,
}) => {
  const [totalDocuments, setTotalDocuments] = useState<number | null>(null);
  const { getDocumentCount } = useDocument(member.id);

  useEffect(() => {
    if (!open || !member?.id) return;

    getDocumentCount()
      .then((res: any) => setTotalDocuments(res?.count ?? 0))
      .catch(() => setTotalDocuments(0));
  }, [open, member?.id]);

  return (
    <MDialog
      isOpen={open}
      onClose={onClose}
      title=""
      primaryCtaText=""
      onPrimaryCta={() => {}}
      showPrimaryCta={false}
      showCloseButton={false}
    >
      <div className={styles.dialogRoot}>
        <button className={styles.closeBtn} onClick={onClose}>
          <IconX size={20} />
        </button>
        <div className={styles.topSection}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              marginTop: 35,
            }}
          >
            <div
              style={{
                width: 104,
                height: 104,
                borderRadius: 80,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MAvatar size="xl" px={104} type="no-dp" text={member.name} />
            </div>
            <div
              style={{
                marginTop: 12,
                marginBottom: 0,
                display: "flex",
                gap: 8,
              }}
            >
              {isOwner ? (
                <MBadge variant="clear" className="w-auto">
                  Family Owner
                </MBadge>
              ) : registrationLabel === "Registered" ? (
                <MBadge variant="purple-inverse" className="w-auto">
                  Registered
                </MBadge>
              ) : registrationLabel === "Invited" ||
                registrationLabel === "Unregistered" ? (
                <MBadge variant="orange-alert" className="w-auto">
                  {registrationLabel}
                </MBadge>
              ) : null}
            </div>
            {member.isPet && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                {member.petType === "cat" ? (
                  <IconCat size={16} color="#45168C" />
                ) : member.petType === "dog" ? (
                  <IconDog size={16} color="#45168C" />
                ) : null}
              </div>
            )}
          </div>
          <div className={styles.topFields}>
            <MInputField
              dialog={true}
              label="Total documents in Maatra"
              value={totalDocuments === null ? "" : totalDocuments.toString()}
              state="Entered"
              variant="Underlined"
              size="md"
              readOnly
            />
            <MInputField
              dialog={true}
              label="Last document added on"
              value={
                member.lastDocumentDate
                  ? member.totalDocuments === 0
                    ? "-"
                    : formatPrettyDate(member.lastDocumentDate)
                  : "Not Applicable"
              }
              state="Entered"
              variant="Underlined"
              size="md"
              readOnly
            />
            <MInputField
              dialog={true}
              label="Emergency card unlocked"
              value={
                typeof member.emergencyCardReady === "boolean"
                  ? member.emergencyCardReady
                    ? "Yes"
                    : "No"
                  : "Not Answered"
              }
              state="Entered"
              variant="Underlined"
              size="md"
              readOnly
            />
          </div>
        </div>
        <div className={styles.bottomSection}>
          <MInputField
            dialog={true}
            label="Name"
            value={member.name || "Not Answered"}
            state="Entered"
            variant="Underlined"
            size="md"
            readOnly
          />
          <MInputField
            dialog={true}
            label="Birth Details"
            value={member.birthDetails || "Not Answered"}
            state="Entered"
            variant="Underlined"
            size="md"
            readOnly
          />
          {!member.isPet && registrationLabel !== "Unregistered" && (
            <>
              <MInputField
                dialog={true}
                label="Mobile number"
                value={member.mobile || "Not Answered"}
                state="Entered"
                variant="Underlined"
                size="md"
                readOnly
              />
              <MInputField
                dialog={true}
                label="Email"
                value={member.email || "Not Answered"}
                state="Entered"
                variant="Underlined"
                size="md"
                readOnly
              />
            </>
          )}
        </div>
      </div>
    </MDialog>
  );
};

export default MemberProfileDialog;
